import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { subscriptionService, Subscription, normalizeSubscriptionPlan } from '@/firebase/subscriptionService';

interface SubscriptionContextType {
  subscription: Subscription | null;
  isLoading: boolean;
  hasActiveSubscription: boolean;
  hasFeature: (feature: 'ai_assistance' | 'asset_management' | 'qr_codes' | 'api' | 'sso' | 'forms' | 'checklists' | 'messaging' | 'assets' | 'create_custom_tasks' | 'compliance_auto_tasks' | 'compliance_notifications' | 'contracts_management' | 'client_side_encryption' | 'cmek' | 'vpc_service_controls' | 'immutable_audit_logging') => boolean;
  refreshSubscription: () => Promise<void>;
  canAddLocation: (currentLocationCount: number) => Promise<{ canAdd: boolean; maxLocations?: number; error?: string }>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadSubscription = useCallback(async () => {
    if (!currentOrganization?.id || !isAuthenticated) {
      console.log('⚠️ Cannot load subscription:', {
        hasOrganization: !!currentOrganization?.id,
        isAuthenticated,
        organizationId: currentOrganization?.id,
      });
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    console.log('📥 Loading subscription for organization:', currentOrganization.id);
    setIsLoading(true);
    try {
      // Pass userId for fallback check of legacy subscriptions
      const result = await subscriptionService.getSubscription(currentOrganization.id, user?.id);
      console.log('📥 Subscription service result:', result);
      if (result.success && result.data) {
        console.log('✅ Subscription loaded:', result.data);
        setSubscription(result.data);
      } else {
        console.log('⚠️ No subscription found or error:', result.error);
        setSubscription({
          plan: null,
          status: 'incomplete',
          quantity: 0,
        });
      }
    } catch (error) {
      console.error('❌ Error loading subscription:', error);
      setSubscription({
        plan: null,
        status: 'incomplete',
        quantity: 0,
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization?.id, isAuthenticated, user?.id]);

  // Set up real-time listener for subscription changes
  useEffect(() => {
    if (!currentOrganization?.id || !isAuthenticated) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    console.log('🔄 Setting up real-time subscription listener for organization:', currentOrganization.id);
    const unsubscribe = subscriptionService.getSubscriptionByOrganizationId(
      currentOrganization.id,
      (sub) => {
        console.log('📥 SubscriptionContext - Callback received subscription:', sub);
        console.log('📥 SubscriptionContext - Subscription details:', {
          plan: sub?.plan,
          status: sub?.status,
          quantity: sub?.quantity,
          hasPlan: !!sub?.plan,
          hasStatus: !!sub?.status,
        });
        const subscriptionToSet = sub || {
          plan: null,
          status: 'incomplete' as const,
          quantity: 0,
        };
        console.log('💾 SubscriptionContext - Setting subscription state:', subscriptionToSet);
        setSubscription(subscriptionToSet);
        setIsLoading(false);
      },
      user?.id // Pass userId for fallback check
    );

    return () => {
      unsubscribe();
    };
  }, [currentOrganization?.id, isAuthenticated, user?.id]);

  // Check if subscription is active (including grace period for past_due)
  const hasActiveSubscription = subscription ? (() => {
    const status = subscription.status;
    
    // Allow active and trialing subscriptions
    if (status === 'active' || status === 'trialing') {
      return true;
    }
    
    // Allow past_due subscriptions within grace period (7 days)
    if (status === 'past_due') {
      const GRACE_PERIOD_DAYS = 7;
      const paymentFailedAt = subscription.paymentFailedAt;
      
      if (paymentFailedAt) {
        try {
          const failedDate = paymentFailedAt.toDate ? paymentFailedAt.toDate() : new Date(paymentFailedAt);
          const daysSinceFailure = (Date.now() - failedDate.getTime()) / (1000 * 60 * 60 * 24);
          return daysSinceFailure <= GRACE_PERIOD_DAYS;
        } catch (error) {
          // If we can't calculate, use updatedAt as fallback
          const updatedAt = subscription.updatedAt;
          if (updatedAt) {
            try {
              const updatedDate = updatedAt.toDate ? updatedAt.toDate() : new Date(updatedAt);
              const daysSinceUpdate = (Date.now() - updatedDate.getTime()) / (1000 * 60 * 60 * 24);
              return daysSinceUpdate <= GRACE_PERIOD_DAYS;
            } catch (e) {
              return false;
            }
          }
        }
      } else {
        // If no paymentFailedAt, check updatedAt as fallback
        const updatedAt = subscription.updatedAt;
        if (updatedAt) {
          try {
            const updatedDate = updatedAt.toDate ? updatedAt.toDate() : new Date(updatedAt);
            const daysSinceUpdate = (Date.now() - updatedDate.getTime()) / (1000 * 60 * 60 * 24);
            return daysSinceUpdate <= GRACE_PERIOD_DAYS;
          } catch (e) {
            return false;
          }
        }
      }
      return false;
    }
    
    return false;
  })() : false;

  const hasFeature = (
    feature: 'ai_assistance' | 'asset_management' | 'qr_codes' | 'api' | 'sso' | 'forms' | 'checklists' | 'messaging' | 'assets' | 'create_custom_tasks' | 'compliance_auto_tasks' | 'compliance_notifications' | 'contracts_management' | 'client_side_encryption' | 'cmek' | 'vpc_service_controls' | 'immutable_audit_logging'
  ): boolean => {
    // Messaging is available for free tier (Communicate) - check this first before subscription status
    if (feature === 'messaging') {
      // If no subscription, allow messaging (free tier)
      if (!subscription || !subscription.plan) {
        return true; // Free tier has messaging access
      }
      
      // Explicitly deny incomplete_expired status
      if (subscription.status === 'incomplete_expired') {
        return false;
      }

      // Normalize plan for backward compatibility (standard -> comply)
      const plan = normalizeSubscriptionPlan(subscription.plan);
      return plan === 'communicate' || plan === 'operate' || plan === 'comply';
    }
    
    // For all other features, require active subscription
    if (!hasActiveSubscription || !subscription?.plan) {
      return false;
    }
    
    // Explicitly deny incomplete_expired status
    if (subscription.status === 'incomplete_expired') {
      return false;
    }

    // Normalize plan for backward compatibility (standard -> comply)
    const plan = normalizeSubscriptionPlan(subscription.plan);

    // Tier 1: Communicate (free) - only team communication
    // Tier 2: Operate - can create tasks from checklists/forms/messages, but no compliance auto-tasks/notifications, no contracts
    // Tier 3: Comply - full access including compliance features and contracts

    switch (feature) {
      
      // Legacy features - maintain backward compatibility
      case 'ai_assistance':
      case 'asset_management':
      case 'qr_codes':
      case 'forms':
      case 'checklists':
      case 'assets':
        // These features are available in operate and comply tiers
        return plan === 'operate' || plan === 'comply';

      case 'api':
      case 'sso':
        // These features are only in comply tier
        return plan === 'comply';

      // New tier-specific features
      case 'create_custom_tasks':
        // Can create tasks from checklists, forms, messages in operate and comply tiers
        return plan === 'operate' || plan === 'comply';

      case 'compliance_auto_tasks':
        // Compliance tasks are automatically generated only in comply tier
        return plan === 'comply';

      case 'compliance_notifications':
        // Compliance notifications are sent only in comply tier
        return plan === 'comply';

      case 'contracts_management':
        // Contracts management is only available in comply tier
        return plan === 'comply';

      // Enterprise Plan Only Features (Bank-Grade Security)
      case 'client_side_encryption':
      case 'cmek':
      case 'vpc_service_controls':
      case 'immutable_audit_logging':
        // These features are ONLY available in enterprise tier
        return plan === 'enterprise';

      default:
        return false;
    }
  };

  const refreshSubscription = useCallback(async () => {
    await loadSubscription();
  }, [loadSubscription]);

  const canAddLocation = useCallback(async (currentLocationCount: number): Promise<{ canAdd: boolean; maxLocations?: number; error?: string }> => {
    if (!currentOrganization?.id) {
      return { canAdd: false, error: 'No organization selected' };
    }
    return await subscriptionService.canAddLocation(currentOrganization.id, currentLocationCount);
  }, [currentOrganization?.id]);

  const value: SubscriptionContextType = {
    subscription,
    isLoading,
    hasActiveSubscription,
    hasFeature,
    refreshSubscription,
    canAddLocation,
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

